// ============================================================
// LiveUpdateNote.tsx — (D12 live updates) the slim one-liner a card shows
// when someone else changed its data while this user is editing it:
//   "Updated by Ollie just now — Reload"
// The draft is never replaced behind the user's back (hooks/useLiveEditNotice).
// Reload discards the draft and re-reads — after a confirm (the house
// ConfirmDialogue) when the draft has unsaved changes. Saving instead still
// goes through the card's own lost-update check (baseUpdatedAt / 409).
// Pair with useLiveEditNotice:
//   const live = useLiveEditNotice(caseId, ['litigationFriend'], editing)
//   <LiveUpdateNote notice={live.notice} dirty={dirty} onReload={() => { discard(); reload(); live.clear() }} />
// ============================================================
import React, { useState } from 'react'
import ActionBox from './ActionBox'
import ConfirmDialogue from './ConfirmDialogue'
import type { LiveEditNotice } from '../../hooks/useLiveEditNotice'

export const LIVE_UPDATE_RELOAD_TITLE = 'Discard your changes?'

const LiveUpdateNote: React.FC<{
  notice: LiveEditNotice | null
  // The card has unsaved changes → Reload asks first.
  dirty: boolean
  onReload: () => void
  className?: string
}> = ({ notice, dirty, onReload, className = '' }) => {
  const [asking, setAsking] = useState(false)
  if (!notice) return null
  const reload = (): void => { if (dirty) setAsking(true); else onReload() }
  return (
    <>
      <ActionBox
        variant="slim"
        tone="info"
        icon="info"
        live
        ariaLabel="Updated by someone else"
        className={className}
        message={<>Updated by <b className="font-semibold text-casevo-forest">{notice.by}</b> just now</>}
        actions={[{ label: 'Reload', kind: 'link', onClick: reload }]}
      />
      <ConfirmDialogue
        isOpen={asking}
        title={LIVE_UPDATE_RELOAD_TITLE}
        message={`Reloading shows ${notice.by === 'someone' ? 'the latest version' : `${notice.by}’s update`} and throws away what you’ve changed on this card.`}
        confirmLabel="Discard and reload"
        cancelLabel="Keep editing"
        onConfirm={() => { setAsking(false); onReload() }}
        onCancel={() => setAsking(false)}
      />
    </>
  )
}

export default LiveUpdateNote
